import { useMemo } from "react"
import { BridgeTransactionHistory } from "@mezo/api"
import {
  Block,
  Button,
  ListItem,
  ListItemLabel,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { DATABASE_DECIMALS_COUNT, IS_MEZO_MAINNET } from "#/shared/constants"
import { useTokenUsdPrice } from "#/shared/hooks"
import {
  bigIntToHumanReadableFormat,
  getCurrencySymbol,
  getTokenIcon,
  multiplyBigIntWithDecimal,
  separateThousandsWithComma,
} from "#/shared/utils"

type OverviewBridgeTransactionItemProps = Pick<
  BridgeTransactionHistory,
  "amount" | "token" | "transactionHash"
>

export default function OverviewBridgeTransactionItem({
  amount,
  token,
  transactionHash,
}: OverviewBridgeTransactionItemProps) {
  const { getTokenUsdPrice } = useTokenUsdPrice()
  const [, theme] = useStyletron()

  const explorerLink = useMemo(() => {
    if (transactionHash.startsWith("0x")) {
      return `https://${
        IS_MEZO_MAINNET ? "" : "sepolia."
      }etherscan.io/tx/${transactionHash}`
    }

    return `https://mempool.space/${
      IS_MEZO_MAINNET ? "" : "testnet/"
    }tx/${transactionHash}`
  }, [transactionHash])

  const tokenUsdValue = useMemo(
    () =>
      amount && token !== "BTC"
        ? bigIntToHumanReadableFormat(
            multiplyBigIntWithDecimal(
              BigInt(amount),
              Number(getTokenUsdPrice(token)),
            ),
            DATABASE_DECIMALS_COUNT,
            2,
          )
        : null,
    [getTokenUsdPrice, token, amount],
  )

  return (
    <ListItem
      overrides={{
        ArtworkContainer: {
          style: () => ({ width: "80px", height: "80px" }),
        },
        Content: { style: () => ({ borderBottom: "none" }) },
        Root: {
          style: () => ({
            borderBottom: `1px solid ${theme.colors.borderOpaque}`,
          }),
        },
      }}
      artwork={() => (
        <Block as="img" src={getTokenIcon(token)} width="48px" height="48px" />
      )}
      endEnhancer={() => (
        <Button
          $as="a"
          shape="pill"
          size="small"
          kind="secondary"
          href={explorerLink}
          target="_blank"
        >
          See on Explorer
        </Button>
      )}
    >
      <ListItemLabel
        description={
          <>
            {tokenUsdValue && (
              <ParagraphSmall
                as="span"
                display="block"
                color={theme.colors.contentSecondary}
              >
                ${separateThousandsWithComma(tokenUsdValue.toString())}
              </ParagraphSmall>
            )}
            <ParagraphSmall
              as="span"
              display="block"
              color={theme.colors.contentPositive}
            >
              Bridge transaction
            </ParagraphSmall>
          </>
        }
      >
        {getCurrencySymbol(token)}
      </ListItemLabel>
    </ListItem>
  )
}
